import React, { useState } from 'react';
import {
    Table,
    Typography,
    Tag,
    Tooltip,
    Button,
    Space,
    Badge,
    Input,
    Select
} from '@douyinfe/semi-ui';
import {
    IconSidebar,
    IconChevronDown,
    IconSearch,
    IconFilter
} from '@douyinfe/semi-icons';
import { formatAmountZhCn, formatDate, renderRepaymentPlanStatusTag, renderPaidMethodTag, renderLoanStatusTag   } from "../../../utils";

const { Text } = Typography;

export interface LoanPlanTableProps {
    data: any[];
    loading?: boolean;
}

const LoanPlanTable: React.FC<LoanPlanTableProps> = ({ data, loading = false }) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<(string | number)[]>([]);
    const [searchText, setSearchText] = useState('');
    const [selectedLoanType, setSelectedLoanType] = useState<string>('');
    const [showExpandedRow, setShowExpandedRow] = useState(false);
    
    // 处理行展开
    const handleExpand = (expanded: boolean, record: any) => {
        const keys = [...expandedRowKeys];
        if (expanded) {
            keys.push(record.nodeId);
        } else {
            const index = keys.indexOf(record.nodeId);
            if (index > -1) {
                keys.splice(index, 1);
            }
        }
        setExpandedRowKeys(keys);
    };
    
    // 渲染贷款类型标签
    const renderLoanTypeTag = (type: string) => {
        const typeConfig: Record<string, { color: any; text: string }> = {
            'MORTGAGE': { color: 'blue', text: '信贷' },
            'CARD': { color: 'green', text: '信用卡' },
            'BORROW': { color: 'orange', text: '借款' }
        };
        
        const config = typeConfig[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
    };
    
    
    // 过滤数据
    const filteredData = data.filter(item => {
        // 按搜索文本过滤
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            return (
                item.platform?.toLowerCase().includes(searchLower) ||
                item.loanNo?.toLowerCase().includes(searchLower) ||
                item.loanType?.toLowerCase().includes(searchLower)
            );
        }
        
        // 按贷款类型过滤
        if (selectedLoanType) {
            return item.loanType === selectedLoanType;
        }
        
        return true;
    });
    
    // 贷款列定义
    const loanColumns = [
        {
            title: '平台',
            dataIndex: 'platform',
            key: 'platform',
            width: 150,
            render: (text: string) => (
                <Text strong>{text}</Text>
            )
        },
        {
            title: '贷款类型',
            dataIndex: 'loanType',
            key: 'loanType',
            width: 100,
            render: renderLoanTypeTag
        },
        {
            title: '贷款金额',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (value: number) => (
                <Text strong type="danger">
                    {formatAmountZhCn(value)}
                </Text>
            )
        },
        {
            title: '已还金额',
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            width: 120,
            render: (value: number) => (
                <Text type="success">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        },
        {
            title: '利息',
            dataIndex: 'interest',
            key: 'interest',
            width: 100,
            render: (value: number) => (
                <Text type="warning">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        },
        {
            title: '抽佣',
            dataIndex: 'commission',
            key: 'commission',
            width: 100,
            render: (value: number) => (
                <Text type="success">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        },
        {
            title: '计划数',
            key: 'planCount',
            width: 80,
            render: (record: any) => (
                <Badge count={record.childCount || 0} />
            )
        },
        {
            title: '状态',
            dataIndex: 'mortgageStatus',
            key: 'mortgageStatus',
            width: 80,
            render: (value:any,record: any) => renderLoanStatusTag(value, record),
        },
        // {
        //     title: '操作',
        //     key: 'action',
        //     width: 100,
        //     render: (record: any) => (
        //         <Button
        //             type="tertiary"
        //             icon={
        //                 expandedRowKeys.includes(record.nodeId) ? 
        //                 <IconEyeClosed /> : 
        //                 <IconEyeOpened />
        //             }
        //             onClick={() => handleExpand(
        //                 !expandedRowKeys.includes(record.nodeId),
        //                 record
        //             )}
        //         >
        //             {expandedRowKeys.includes(record.nodeId) ? '收起' : '展开'}
        //         </Button>
        //     )
        // }
    ];
    
    // 还款计划列定义
    const planColumns = [
        {
            title: '期数',
            dataIndex: 'periodNo',
            key: 'periodNo',
            width: 80,
            render: (text: number) => `第${text}期`
        },
        {
            title: '应还金额',
            dataIndex: 'oneTermAmount',
            key: 'oneTermAmount',
            width: 100,
            render: (value: number) => formatAmountZhCn(value)
        },
        {
            title: '实还金额',
            dataIndex: 'planPaidAmount',
            key: 'planPaidAmount',
            width: 100,
            render: (value: number) => (
                <Text strong type="success">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        },
        {
            title: '还款日期',
            dataIndex: 'paidDate',
            key: 'paidDate',
            width: 120,
            render: (value: string) => value ? formatDate(value) : '-'
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (value: any, record:any) => renderRepaymentPlanStatusTag(value, record),
        },
        {
            title: '还款方式',
            dataIndex: 'paidMethod',
            key: 'paidMethod',
            width: 120,
            render: (value: any, record:any) => renderPaidMethodTag(value),
        },
        {
            title: '利息天数',
            dataIndex: 'interestDays',
            key: 'interestDays',
            width: 90,
            render: (value: number) => value ? `${value}天` : '-'
        },
        {
            title: '利息',
            dataIndex: 'planInterest',
            key: 'planInterest',
            width: 100,
            render: (value: number) => (
                <Text type="warning">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        },
        {
            title: '抽佣',
            dataIndex: 'planCommission',
            key: 'planCommission',
            width: 100,
            render: (value: number) => (
                <Text type="success">
                    {formatAmountZhCn(value || 0)}
                </Text>
            )
        }
    ];
    
    return (
        <div className="loan-plan-table">
            <div className="table-header">
                <Space>
                    <Input
                        prefix={<IconSearch />}
                        style={{width:'200px'}}
                        placeholder="搜索平台、贷款编号..."
                        value={searchText}
                        onChange={setSearchText}
                        showClear
                    />
                    
                    <Select
                        placeholder="筛选贷款类型"
                        value={selectedLoanType}
                        onChange={setSelectedLoanType}
                        style={{width:'120px'}}
                        optionList={[
                            { label: '全部', value: '' },
                            { label: '信贷', value: 'MORTGAGE' },
                            { label: '信用卡', value: 'CARD' },
                            { label: '借款', value: 'BORROW' }
                        ]}
                        prefix={<IconFilter />}
                        showClear
                    />
                    <Button onClick={()=>setShowExpandedRow(!showExpandedRow)} icon={showExpandedRow?<IconSidebar />:<IconChevronDown />} theme="outline" style={{ marginRight: 10 }}>{showExpandedRow?"收起全部":"展开全部"}</Button>
                    <Button type="secondary" disabled={loading}>
                        共 {filteredData.length} 笔贷款
                    </Button>
                </Space>
            </div>
            
            <Table
                columns={loanColumns}
                dataSource={filteredData}
                rowKey="nodeId"
                loading={loading}
                pagination={false}
                scroll={{ y: 600 }}
                expandAllRows={showExpandedRow}
                expandRowByClick
                expandedRowKeys={expandedRowKeys}
                onExpand={(expanded, record) => handleExpand(expanded, record)}
                expandedRowRender={(record: any) => {
                    if (!record.childrenNodes || record.childrenNodes.length === 0) {
                        return (
                            <div className="empty-plan">
                                <Text type="tertiary">暂无还款计划</Text>
                            </div>
                        );
                    }
                    
                    return (
                        <Table
                            columns={planColumns}
                            dataSource={record.childrenNodes}
                            rowKey="nodeId"
                            size="small"
                            pagination={false}
                            scroll={{ y: 200 }}
                        />
                    );
                }}
            />
        </div>
    );
};

export default LoanPlanTable;